// Management screen for platform users (see `features/users-permissions.md`):
// list every user within the caller's scope with their roles, company access
// and blocked state; create and edit users (via `UserDialog`), block/unblock
// them, and — Super Admins only — delete them. A company-scoped manager (e.g.
// a Company Admin) sees the users of their own companies (their athletes' and
// coaches' logins included), and instead of deleting can only unlink a user
// from their companies — the account stays, managed by Super Admins. The list
// can be narrowed to users linked to one company (Super Admins act on every
// company, so they match any filter). Reached from the top-level app menu;
// each action button is gated by its own permission, and the server enforces
// the same rules regardless.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PersonIcon from '@mui/icons-material/Person';
import type { PermissionInfo, RoleInfo, UserAccount } from '../domain';
import {
  PLATFORM_PERMISSION_IDS,
  companyGrantableRoles,
  isSuperAdmin,
} from '../domain';
import {
  deleteUser,
  fetchPermissions,
  fetchRoles,
  fetchUsers,
  updateUser,
} from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { useTournament } from '../state/TournamentContext';
import { UserDialog } from './UserDialog';

// Prefer the locale catalogue entry for a role/permission; unknown (custom)
// entries fall back to the server-provided English text.
function catalogText(
  t: (key: string) => string,
  key: string,
  fallback: string,
): string {
  const translated = t(key);
  return translated === key ? fallback : translated;
}

export function UsersView() {
  const { me, hasPermission, refreshMe } = useAuth();
  const { companies } = useTournament();
  const { t } = useTranslation();

  const [users, setUsers] = useState<UserAccount[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [permissions, setPermissions] = useState<PermissionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UserAccount | null>(null);
  const [pendingDelete, setPendingDelete] = useState<UserAccount | null>(null);
  const [pendingUnlink, setPendingUnlink] = useState<UserAccount | null>(null);
  const [busy, setBusy] = useState(false);
  // Company filter: 'all' shows every user.
  const [companyFilter, setCompanyFilter] = useState<number | 'all'>('all');

  // Whether the caller acts platform-wide (Super Admin) or within their own
  // companies. Deleting is Super-Admin-only — a company-scoped manager unlinks
  // users from their companies instead (the server refuses a scoped delete).
  const actsOnAllCompanies = me?.allCompanies === true;
  const canCreate = hasPermission('CREATE_USER');
  const canEdit = hasPermission('EDIT_USER');
  const canDelete = hasPermission('DELETE_USER') && actsOnAllCompanies;
  const canUnlink = canEdit && !actsOnAllCompanies;
  const myCompanyIds = useMemo(
    () => new Set(me?.user.companyIds ?? []),
    [me],
  );

  // The data loaders below must not re-run when the language changes (their
  // effects would refetch and flash the view), so they read the translator
  // through a ref that always holds the latest `t` without changing identity.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    try {
      const [nextUsers, nextRoles, nextPermissions] = await Promise.all([
        fetchUsers(),
        fetchRoles(),
        fetchPermissions(),
      ]);
      setUsers(nextUsers);
      setRoles(nextRoles);
      setPermissions(nextPermissions);
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.users.errors.load'),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const roleNames = useMemo(
    () => new Map(roles.map((role) => [role.id, role.name])),
    [roles],
  );
  const companyNames = useMemo(
    () => new Map(companies.map((company) => [company.id, company.name])),
    [companies],
  );

  // A company-scoped manager is offered only the roles a club hands out —
  // Company Admin and Coach (no Super Admin, no hand-assigned Athlete; see
  // `companyGrantableRoles`). Mirrors `CompanyProfileView`.
  const grantableRoles = useMemo(
    () => (actsOnAllCompanies ? roles : companyGrantableRoles(roles)),
    [roles, actsOnAllCompanies],
  );
  const grantablePermissions = useMemo(
    () =>
      actsOnAllCompanies
        ? permissions
        : permissions.filter((p) => !PLATFORM_PERMISSION_IDS.includes(p.id)),
    [permissions, actsOnAllCompanies],
  );
  // A scoped manager must link every new user to one of their companies (the
  // server refuses an unlinked create), so pre-select them all.
  const initialCompanyIds = useMemo(
    () => (actsOnAllCompanies ? undefined : (me?.user.companyIds ?? [])),
    [actsOnAllCompanies, me],
  );

  // Super Admins act on every company, so they match any company filter.
  const visible = useMemo(
    () =>
      companyFilter === 'all'
        ? users
        : users.filter(
            (user) =>
              isSuperAdmin(user) || user.companyIds.includes(companyFilter),
          ),
    [users, companyFilter],
  );

  // After a save that may concern the signed-in user, refresh both the list
  // and the caller's own access picture (their buttons may change).
  const afterChange = useCallback(
    async (touchedSelf: boolean) => {
      await load();
      if (touchedSelf) await refreshMe();
    },
    [load, refreshMe],
  );

  const toggleBlocked = async (user: UserAccount) => {
    setBusy(true);
    setError(null);
    try {
      await updateUser(user.id, { blocked: user.blockedAt === null });
      await afterChange(user.id === me?.user.id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.users.errors.update'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    setBusy(true);
    setError(null);
    try {
      setUsers(await deleteUser(target.id));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.users.errors.delete'));
    } finally {
      setBusy(false);
    }
  };

  // Remove the caller's own companies from the target's links, leaving any
  // links to other companies untouched (the server refuses touching those
  // anyway). The account itself stays — only a Super Admin can delete it.
  const confirmUnlink = async () => {
    if (!pendingUnlink) return;
    const target = pendingUnlink;
    setPendingUnlink(null);
    setBusy(true);
    setError(null);
    try {
      await updateUser(target.id, {
        companyIds: target.companyIds.filter((id) => !myCompanyIds.has(id)),
      });
      await afterChange(target.id === me?.user.id);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.users.errors.update'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t('admin.users.title')}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="users-company-filter-label">
            {t('admin.users.companyFilter.label')}
          </InputLabel>
          <Select
            labelId="users-company-filter-label"
            label={t('admin.users.companyFilter.label')}
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value as number | 'all')}
          >
            <MenuItem value="all">{t('admin.users.companyFilter.all')}</MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            {t('admin.users.newUser')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('admin.users.empty')}
          </Typography>
        </Paper>
      ) : visible.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('admin.users.emptyFiltered')}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {visible.map((user) => {
            const self = user.id === me?.user.id;
            const allCompanies = isSuperAdmin(user);
            const blocked = user.blockedAt !== null;
            return (
              <Paper
                key={user.id}
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  opacity: blocked ? 0.6 : 1,
                }}
              >
                <PersonIcon color={blocked ? 'disabled' : 'primary'} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" noWrap title={user.email}>
                      {user.name || user.email}
                    </Typography>
                    {self && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('admin.common.you')}
                      />
                    )}
                    {blocked && (
                      <Chip
                        size="small"
                        color="error"
                        label={t('admin.users.blockedChip')}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user.email} ·{' '}
                    {allCompanies
                      ? t('admin.users.allCompanies')
                      : user.companyIds.length === 0
                        ? t('admin.users.noCompanies')
                        : user.companyIds
                            .map(
                              (id) =>
                                companyNames.get(id) ??
                                t('admin.users.companyFallback', { id }),
                            )
                            .join(', ')}
                  </Typography>
                </Box>
                {user.roleIds.map((roleId) => (
                  <Chip
                    key={roleId}
                    size="small"
                    color={roleId === 'SUPER_ADMIN' ? 'secondary' : 'default'}
                    label={catalogText(
                      t,
                      `admin.access.roles.${roleId}.name`,
                      roleNames.get(roleId) ?? roleId,
                    )}
                  />
                ))}
                {canEdit && (
                  <Tooltip title={t('admin.users.editUser', { email: user.email })}>
                    <span>
                      <IconButton
                        aria-label={t('admin.users.editUser', { email: user.email })}
                        disabled={busy}
                        onClick={() => {
                          setEditing(user);
                          setDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canEdit && (
                  <Tooltip
                    title={
                      self
                        ? t('admin.users.blockSelf')
                        : blocked
                          ? t('admin.users.unblockUser', { email: user.email })
                          : t('admin.users.blockUser', { email: user.email })
                    }
                  >
                    <span>
                      <IconButton
                        aria-label={
                          blocked
                            ? t('admin.users.unblockUser', { email: user.email })
                            : t('admin.users.blockUser', { email: user.email })
                        }
                        disabled={busy || (self && !blocked)}
                        onClick={() => void toggleBlocked(user)}
                      >
                        {blocked ? (
                          <LockOpenIcon fontSize="small" />
                        ) : (
                          <BlockIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canUnlink && user.companyIds.some((id) => myCompanyIds.has(id)) && (
                  <Tooltip
                    title={
                      self
                        ? t('admin.users.unlinkSelf')
                        : t('admin.users.unlinkUser', { email: user.email })
                    }
                  >
                    <span>
                      <IconButton
                        aria-label={t('admin.users.unlinkUser', {
                          email: user.email,
                        })}
                        disabled={busy || self}
                        onClick={() => setPendingUnlink(user)}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title={self ? t('admin.users.deleteSelf') : ''}>
                    <span>
                      <IconButton
                        aria-label={t('admin.users.deleteUser', {
                          email: user.email,
                        })}
                        disabled={busy || self}
                        onClick={() => setPendingDelete(user)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      <UserDialog
        open={dialogOpen}
        user={editing}
        roles={grantableRoles}
        permissions={grantablePermissions}
        companies={companies}
        initialCompanyIds={initialCompanyIds}
        onClose={() => setDialogOpen(false)}
        onSaved={(saved) => void afterChange(saved.id === me?.user.id)}
      />

      <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)}>
        <DialogTitle>{t('admin.users.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDelete
              ? t('admin.users.deleteDialog.body', { email: pendingDelete.email })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>
            {t('admin.common.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void confirmDelete()}>
            {t('admin.common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingUnlink !== null} onClose={() => setPendingUnlink(null)}>
        <DialogTitle>{t('admin.users.unlinkDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingUnlink
              ? t('admin.users.unlinkDialog.body', { email: pendingUnlink.email })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingUnlink(null)}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmUnlink()}
          >
            {t('admin.users.unlinkDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
